import type { Channel } from '../../../shared/services/vendor-channel-resolver.service';
import {
  isRollupVendorKey,
  resolveVendor,
} from '../../../shared/services/vendor-channel-resolver.service';
import type { EAN } from '../shared/ean';
import { HIDDEN_PORTAL_VENDORS } from '../shared/hidden-portal-vendors';
import {
  DEFAULT_OUT_OF_STOCK_PRICE,
  DEFAULT_PRICE_FIELD_CONVENTION,
  DEFAULT_REFERENCE_KIND,
  DEFAULT_STOCK_REPORTING,
  outOfStockPriceOf,
  type PriceFieldConvention,
  priceFieldConventionOf,
  referenceKindOf,
  stockReportingOf,
} from './vendor-price-conventions';

// ---------------------------------------------------------------------------
// Wire types -- single declaration, ACL-owned (PRD AC-04)
// ---------------------------------------------------------------------------

export type PaylVendorRecord = {
  readonly p?: number | null;
  readonly lp?: number | null;
  readonly q?: number | null;
  readonly qs?: number | null;
  readonly rev?: number | null;
};

export type Payl = Readonly<Record<string, Readonly<Record<string, PaylVendorRecord>>>>;

// ---------------------------------------------------------------------------
// Domain model: Observation (PRD AC-01)
// ---------------------------------------------------------------------------

export type ObservedFacts = {
  readonly price: number | null;
  /** The shown list price; undefined at a vendor declared to store no reference, null when the vendor showed none that day. */
  readonly listPrice?: number | null;
  readonly stockLevel: number | null;
  /**
   * The day's stock state, decoded once: true in stock, false out of stock, null unknown.
   * From the level when one is reported; without a reading, true at a vendor that reports
   * stock only at 0 (zero-only) and null at a vendor that reports a level.
   */
  readonly inStock: boolean | null;
};

/** The reference the vendor showed that day, whichever kind it declares, or null when it showed none. */
export function shownReference(observed: ObservedFacts): number | null {
  return observed.listPrice ?? null;
}

export type InferredSignal = {
  readonly unitsSold: number | null;
  readonly revenue: number | null;
};

export type Observation = {
  readonly ean: EAN;
  readonly vendor: string;
  readonly channel: Channel;
  readonly day: string;
  readonly observed: ObservedFacts;
  readonly signal: InferredSignal;
};

/**
 * Ean-less observation shape for brand-grain series (b-/bc-/bps- payloads).
 * Observation is structurally assignable to SeriesObservation, so SKU-grain
 * callers may pass Observation[] unchanged (PRD AC-11, unit 11 decode boundary).
 */
export type SeriesObservation = Omit<Observation, 'ean'>;

// ---------------------------------------------------------------------------
// Decode result -- discriminated union (PRD AC-08)
// ---------------------------------------------------------------------------

export type DecodeSuccess = {
  readonly kind: 'ok';
  readonly observation: Observation;
};

export type DecodeFailure = {
  readonly kind: 'err';
  readonly ean: EAN;
  readonly day: string;
  readonly vendorKey: string;
  readonly reason: string;
};

export type DecodeResult = DecodeSuccess | DecodeFailure;

// ---------------------------------------------------------------------------
// Decode helpers
// ---------------------------------------------------------------------------

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function toNullableNumber(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number' && isFinite(v)) return v;
  return null;
}

function mapPriceFields(
  lp: unknown,
  p: unknown,
  convention: PriceFieldConvention
): { price: number | null; reference: number | null } {
  if (convention === 'lp-list') {
    return { price: toNullableNumber(p), reference: toNullableNumber(lp) };
  }
  const price = toNullableNumber(lp);
  const reference = toNullableNumber(p);
  // A reference of 0 or null is unknown, not the charged price (CONTEXT.md, price-field convention).
  return { price, reference: reference !== null && reference > 0 ? reference : null };
}

// A stock-0 day without a shown reduction carries no price where the vendor's out-of-stock price is unreliable (CONTEXT.md, Price-field convention).
function priceWhenOutOfStock(
  vendor: string,
  stockLevel: number | null,
  fields: { price: number | null; reference: number | null }
): { price: number | null; reference: number | null } {
  const { price, reference } = fields;
  const unreliable =
    (outOfStockPriceOf(vendor) ?? DEFAULT_OUT_OF_STOCK_PRICE) === 'unreliable' && stockLevel === 0;
  if (!unreliable || (reference !== null && price !== null && reference > price)) return fields;
  return { price: null, reference: null };
}

// A missing reading means in stock where the vendor reports stock only at 0, and unknown where it reports a level (CONTEXT.md, Price-field convention).
function deriveInStock(vendor: string, stockLevel: number | null): boolean | null {
  if (stockLevel !== null) return stockLevel > 0;
  return (stockReportingOf(vendor) ?? DEFAULT_STOCK_REPORTING) === 'zero-only' ? true : null;
}

// One shared signal for a record without sales data, so a decode of many such days allocates none.
const EMPTY_SIGNAL: InferredSignal = Object.freeze({ unitsSold: null, revenue: null });

function decodeRow(ean: EAN, day: string, vendorKey: string, record: unknown): DecodeResult {
  if (typeof record !== 'object' || record === null) {
    return {
      kind: 'err',
      ean,
      day,
      vendorKey,
      reason: `vendor record for "${vendorKey}" on ${day} is not an object`,
    };
  }
  const r = record as Record<string, unknown>;
  const { vendor, channel } = resolveVendor(vendorKey);
  const stockLevel = toNullableNumber(r['q']);
  const { price, reference } = priceWhenOutOfStock(
    vendor,
    stockLevel,
    mapPriceFields(
      r['lp'],
      r['p'],
      priceFieldConventionOf(vendor) ?? DEFAULT_PRICE_FIELD_CONVENTION
    )
  );
  const shown =
    (referenceKindOf(vendor) ?? DEFAULT_REFERENCE_KIND) === 'list' ? { listPrice: reference } : {};
  const unitsSold = toNullableNumber(r['qs']);
  const revenue = toNullableNumber(r['rev']);

  return {
    kind: 'ok',
    observation: {
      ean,
      vendor,
      channel,
      day,
      observed: {
        price,
        ...shown,
        stockLevel,
        inStock: deriveInStock(vendor, stockLevel),
      },
      signal: unitsSold === null && revenue === null ? EMPTY_SIGNAL : { unitsSold, revenue },
    },
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Decode a raw payl wire value into typed Observations, one per (day, vendorKey) row.
 *
 * Wire model: top-level keys are either YYYY-MM-DD date strings carrying vendor records,
 * or the metadata pair {_status, reason} emitted by marker rows. The repository merges
 * multiple portal_cache rows per EAN (month partitions + status rows), so mixed payloads
 * containing both metadata keys and date keys are the normal case, not an anomaly.
 *
 * Maps wire lp/p by the vendor's price-field convention and places the reference by its declared kind.
 * Rows that cannot be decoded produce a loud DecodeFailure; siblings always proceed.
 * Top-level structural failures (payl is not an object) produce a single DecodeFailure.
 */
export function decodePayl(ean: EAN, rawPayl: unknown): readonly DecodeResult[] {
  if (typeof rawPayl !== 'object' || rawPayl === null) {
    return [
      {
        kind: 'err',
        ean,
        day: '',
        vendorKey: '',
        reason: 'payl is not an object',
      },
    ];
  }

  // Status-marker-only variant: payloads whose keys are exclusively from
  // {_status, reason} carry no time-series data; return empty immediately.
  const rawKeys = Object.keys(rawPayl);
  if (rawKeys.length > 0 && rawKeys.every((k) => k === '_status' || k === 'reason')) {
    return [];
  }

  const payl = rawPayl as Payl;
  const results: DecodeResult[] = [];

  for (const [day, dayData] of Object.entries(payl)) {
    // _status and reason are known metadata keys of the wire format; skip silently.
    if (day === '_status' || day === 'reason') {
      continue;
    }
    if (!DATE_RE.test(day)) {
      results.push({
        kind: 'err',
        ean,
        day,
        vendorKey: '',
        reason: `day key "${day}" is not a valid YYYY-MM-DD date`,
      });
      continue;
    }
    if (typeof dayData !== 'object' || dayData === null) {
      results.push({
        kind: 'err',
        ean,
        day,
        vendorKey: '',
        reason: `day entry for ${day} is not an object`,
      });
      continue;
    }
    for (const [vendorKey, record] of Object.entries(dayData)) {
      // comb* keys are precomputed rollup aggregates (D-01); exclude silently.
      // Key-shape test on the raw vendor key, never resolveVendor().channel (data-access.md).
      if (isRollupVendorKey(vendorKey)) continue;
      if (HIDDEN_PORTAL_VENDORS.has(vendorKey)) continue;
      results.push(decodeRow(ean, day, vendorKey, record));
    }
  }

  return results;
}

/**
 * Brand-grain decode entry: decodes a b-/bc-/bps- payload where no real EAN
 * is available. The '' as EAN cast is contained here inside the ACL; callers
 * outside the ACL must use decodePayl (SKU-grain, real EAN) or this function
 * (brand-grain, no EAN) — never cast '' as EAN themselves (PRD AC-11, unit 11).
 */
export function decodeBrandPayl(rawPayl: unknown): readonly DecodeResult[] {
  return decodePayl('' as EAN, rawPayl);
}
